import * as Leaflet from 'leaflet'
import * as PropTypes from 'prop-types'
import * as React from 'react'
import * as ReactDOMServer from 'react-dom/server'
import * as Rx from 'rxjs'
import * as log from 'loglevel'
import { identity } from 'lodash'

import DialogueView from './DialogueView'
import ImageView from './ImageView'
import LeaftletMapObjectLayer from './LeaftletMapObjectLayer'
import MapObjectCRS from './MapObjectCRS'
import MapObjectView from './MapObjectView'
import PlayerView from './PlayerView'
import PositionInfo from './PositionInfo'
import WanderCRS from '../../models/WanderCRS'
import animationFrame from '../animationFrame'
import {
  MapObjectEvent,
  SpeakEvent,
  isAbsentEvent,
  isPresentEvent,
  isSpeakEvent
} from '../../io/events'

export interface Props {
  /// Events to be processed
  events$: Rx.Observable<MapObjectEvent>
  wanderCRS: WanderCRS
  focusMapObjectID?: string
  onMapObjectPress?: (id: string) => void
}

export default class MapObjectLayer extends React.Component<Props> {
  static contextTypes = {
    layerContainer: PropTypes.shape({
      addLayer: PropTypes.func.isRequired,
      removeLayer: PropTypes.func.isRequired
    }),
    map: PropTypes.instanceOf(Leaflet.Map).isRequired,
    pane: PropTypes.string
  }

  private readonly logger = log.getLogger('MapObjectLayer')
  private readonly subscription = new Rx.Subscription()
  private readonly layer: LeaftletMapObjectLayer
  private readonly mapObjectViews: { [index: string]: MapObjectView } = {}
  private readonly mapObjectView$ = new Rx.Subject<MapObjectView>()
  private readonly focusMapObjectID$ = new Rx.BehaviorSubject<
    string | undefined
  >(undefined)
  private _mapObjectCRS: MapObjectCRS
  private dialogueDiv: HTMLDivElement | null
  private mapObjectsDiv: HTMLDivElement | null

  get layerContainer (): Leaflet.Map {
    return this.context.layerContainer || this.context.map
  }

  get mapObjectCRS (): MapObjectCRS {
    return this._mapObjectCRS
  }

  constructor (props: Props, context: any) {
    super(props, context)
    this.layer = new LeaftletMapObjectLayer()
    this._mapObjectCRS = new MapObjectCRS(
      this.props.wanderCRS,
      this.layerContainer.getPixelOrigin(),
      this.layerContainer.getZoom()
    )
  }

  componentDidMount () {
    const map = this.layerContainer
    this.layer.addTo(map)
    this.initializeContainer()

    map.boxZoom.disable()
    map.on('zoomend viewreset', this.onMapUpdate, this)

    // update map position based on focus map object
    this.subscription.add(
      this.focusMapObjectID$
        .switchMap(id => {
          if (!id) {
            return Rx.Observable.empty<PositionInfo>()
          }
          const view = this.mapObjectViews[id]
          if (!view) {
            return this.mapObjectView$
              .filter(view => view.id === id)
              .switchMap(view => view.position$)
          }
          return view.position$
        })
        .subscribe(positionInfo => {
          if (!this._mapObjectCRS) {
            return
          }
          // could be the map object is not loaded and present
          if (!positionInfo) {
            return
          }
          const { base } = positionInfo
          const latLng = map.layerPointToLatLng([base.x, base.y])
          map.panTo(latLng, { animate: false })
        })
    )
    this.subscription.add(animationFrame.subscribe(this.updateFrame))

    this.subscription.add(
      this.props.events$
        // group the map object events by its id, end groupping when we see absent
        .groupBy(event => event.mapObjectID, undefined, groupedEvents$ =>
          groupedEvents$.filter(isAbsentEvent)
        )
        .map(event$ =>
          // share subscription from upstream
          event$.multicast<MapObjectEvent>(
            () => new Rx.Subject<MapObjectEvent>(),
            // we want to defer all event other than present until present
            // appears. as sometimes, load data event will appears before
            // present event, but we only create map object view after present,
            // as a result we may miss the load event before present arrive.
            castedEvent$ => {
              const present$ = castedEvent$.filter(isPresentEvent)
              return (
                Rx.Observable
                  .merge(
                    // take the first present
                    present$.take(1),
                    // buffer other events, until first present appears
                    castedEvent$
                      .buffer(present$.take(1))
                      .take(1)
                      .switchMap(events => Rx.Observable.from(events))
                  )
                  // concat by following events
                  .concat(castedEvent$)
              )
            }
          )
        )
        .mergeAll()
        .subscribe(event => {
          if (isPresentEvent(event)) {
            let view: MapObjectView | null = null
            switch (event.mapObjectType) {
              case 'image':
                view = new ImageView(
                  this.mapObjectsDiv!,
                  this.mapObjectCRS,
                  event.mapObjectID,
                  this.onImageViewPress
                )
                break
              case 'player':
                view = new PlayerView(
                  this.mapObjectsDiv!,
                  this.mapObjectCRS,
                  event.mapObjectID
                )
                break
              default:
                this.logger.warn(
                  `Unknown map object type ${event.mapObjectType}`
                )
            }
            if (view) {
              this.mapObjectViews[event.mapObjectID] = view
              view.processEvent(event)
              this.mapObjectView$.next(view)
            }
            return
          }
          const view = this.mapObjectViews[event.mapObjectID]
          if (view) {
            view.processEvent(event)
          } else {
            this.logger.warn(
              'Ignore event with unknown map object ID',
              event.mapObjectID,
              'event=',
              event
            )
          }
        })
    )

    this.subscription.add(
      this.props.events$
        .filter(isSpeakEvent)
        .filter(event => !event.bySelf)
        .subscribe(event => {
          this.addDialogue(event)
        })
    )
  }

  componentWillUnmount () {
    const map = this.layerContainer
    map.off('zoomend viewreset', this.onMapUpdate, this)
    this.layer.remove()
    this.subscription.unsubscribe()
    // TODO: clean layer container?
  }

  componentWillUpdate (nextProps: Props) {
    if (this.props.focusMapObjectID !== nextProps.focusMapObjectID) {
      this.focusMapObjectID$.next(nextProps.focusMapObjectID)
    }
    // disalbe dragging if we have a focus map object
    if (nextProps.focusMapObjectID) {
      this.layerContainer.dragging.disable()
      this.layerContainer.scrollWheelZoom.disable()
      this.layerContainer.touchZoom.disable()
      this.layerContainer.doubleClickZoom.disable()
    } else {
      this.layerContainer.dragging.enable()
      this.layerContainer.scrollWheelZoom.enable()
      this.layerContainer.touchZoom.enable()
      this.layerContainer.doubleClickZoom.enable()
    }
  }

  render (): JSX.Element | null {
    return null
  }

  /**
   * Get current position of map object view
   * @param id id of map object to get position from
   */
  getMapObjectPosition (id: string) {
    const view = this.mapObjectViews[id]
    if (!view) {
      return undefined
    }
    return view.position$.value
  }

  private onImageViewPress = (imageView: ImageView) => {
    if (this.props.onMapObjectPress) {
      this.props.onMapObjectPress(imageView.id)
    }
  }

  private onMapUpdate () {
    const map = this.layerContainer
    this.logger.info(
      'Map updated, pixelOrigin=',
      map.getPixelOrigin(),
      ', zoom=',
      map.getZoom()
    )
    this._mapObjectCRS = new MapObjectCRS(
      this.props.wanderCRS,
      map.getPixelOrigin(),
      map.getZoom()
    )
    for (const key of Object.keys(this.mapObjectViews)) {
      const view = this.mapObjectViews[key]
      view.updateMapObjectCRS(this.mapObjectCRS)
    }
  }

  private updateFrame = (elasped: number) => {
    for (const key of Object.keys(this.mapObjectViews)) {
      const view = this.mapObjectViews[key]
      view.updateFrame(elasped)
    }
  }

  private initializeContainer () {
    this.layer._container.innerHTML = ReactDOMServer.renderToStaticMarkup(
      <div className='relative'>
        <div style={{ position: 'absolute', zIndex: 2000 }} />
        <div style={{ position: 'absolute', zIndex: 1000 }} />
      </div>
    )
    const div = this.layer._container.firstChild
    this.dialogueDiv = div!.childNodes[0] as HTMLDivElement
    this.mapObjectsDiv = div!.childNodes[1] as HTMLDivElement
  }

  private addDialogue (event: SpeakEvent) {
    const playerView = this.mapObjectViews[event.mapObjectID]! as PlayerView
    const nextSpeakEvent = this.props.events$
      .filter(isSpeakEvent)
      .filter(event => !event.bySelf)
      .filter(newEvent => newEvent.mapObjectID === event.mapObjectID)
      .mapTo(undefined)
    const viewRemoved = playerView.removed$.filter(identity).mapTo(undefined)
    const remove = Rx.Observable.merge(nextSpeakEvent, viewRemoved).take(1)
    const view = new DialogueView(
      this.dialogueDiv!,
      playerView.position$
        .filter(identity)
        .map(positionInfo => positionInfo!)
        .takeUntil(viewRemoved),
      remove,
      event.message
    )
    view.mount()
    return view
  }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/MapObjects/MapObjectLayer.tsx